import React, { useState, useEffect, useMemo, useRef } from 'react';
import { usePharmacyPickerContext } from './PharmacyPickerContext';
import { getPrice } from '../../utils/translator';
import * as translator from '../../utils/translator';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';
// Type-only: the cannabis layout imports this picker, so a value import would be a runtime cycle.
import type { Product } from '../../pages/ChooseTreatmentVariants/FreeDosageChooseTreatmentV2/FreeDosageLayoutV2';
import { calculatePricePerGram } from '../../pages/ChooseTreatmentVariants/helpersV2';
interface PharmacySelectionPanelProps {
  pharmaciesLocations: {
    [stockCode: string]: {
      name: string;
      address: string;
      location?: { lat: number; lng: number };
      is_external_pharmacy: boolean;
    };
  };
  availableStocks: { stockCode: string; pricePerUnit: number }[];
  selectedStockCode?: string | null;
  highlightedStockCode?: string | null;
  onPharmacySelect: (stockCode: string) => void;
  product: Product;
}

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const PharmacySelectionPanel: React.FC<PharmacySelectionPanelProps> = ({
  pharmaciesLocations,
  availableStocks,
  selectedStockCode,
  highlightedStockCode,
  onPharmacySelect,
  product,
}) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const language = localStorage.getItem("language") || "de";
  const { locale = 'de-DE' } = usePharmacyPickerContext();
  const { customerState } = useCustomer();

  // Get user location for distance calculation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          // Silently fail - distance just won't be shown
          window.clientLogger?.debug?.(
            'Could not get user location for distance calculation',
            error
          );
        }
      );
    }
  }, []);

  // Track pharmacy selection
  const handlePharmacySelect = (stockCode: string, pharmacy: { stockCode: string; name: string; address: string; location?: { lat: number; lng: number }; pricePerUnit: number; distance: number | null; is_external_pharmacy: boolean }) => {
    const userId = customerState?.customerID || null;
    const distanceKm = pharmacy.distance !== null ? pharmacy.distance : null;

    // Track pharmacy_selected_from_product (product-first flow)
    // Calculate price_per_gram
    const pricePerGram = product.items[0] 
      ? calculatePricePerGram(pharmacy.pricePerUnit, product.items[0], product.quantity_type)
      : null;

    MainTracker.track("custom", "pharmacy_selected_from_product", {
      user_id: userId,
      product_id: product.product_id,
      product_name: product.title,
      pharmacy_id: stockCode,
      pharmacy_name: pharmacy.name,
      distance_km: distanceKm,
      price_per_gram: pricePerGram
    });

    onPharmacySelect(stockCode);
  };

  const pharmaciesWithPricing = useMemo(() => {

    return availableStocks
      .map((stock) => {
        const pharmacy = pharmaciesLocations[stock.stockCode];
        if (!pharmacy) return null;

        let distance: number | null = null;
        if (userLocation && pharmacy.location) {
          distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            pharmacy.location.lat,
            pharmacy.location.lng
          );
        }

        return {
          stockCode: stock.stockCode,
          name: pharmacy.name,
          address: pharmacy.address,
          location: pharmacy.location,
          pricePerUnit: stock.pricePerUnit,
          distance,
          is_external_pharmacy: pharmacy.is_external_pharmacy,
        };
      })
      .filter((p) => p !== null)
      .sort((a, b) => {
        // Sort by distance if available, otherwise by name
        if (a!.distance !== null && b!.distance !== null) {
          return a!.distance - b!.distance;
        }
        if (a!.distance !== null) return -1;
        if (b!.distance !== null) return 1;
        return a!.name.localeCompare(b!.name);
      });
  }, [pharmaciesLocations, availableStocks, userLocation]);

  // Mouse drag handlers for desktop scrolling
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    
    // Don't start drag if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = x - startX;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  // Pharmacy selection panel horizontal scroll handler
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Prevent page scroll and convert vertical scroll to horizontal
      e.preventDefault();
      e.stopPropagation();
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Auto-scroll to highlighted pharmacy
  useEffect(() => {
    if (highlightedStockCode && scrollContainerRef.current) {
      const highlightedIndex = pharmaciesWithPricing.findIndex(
        (p) => p!.stockCode === highlightedStockCode
      );
      if (highlightedIndex !== -1) {
        const cardWidth = 300 + 16; // card width + gap
        const scrollTo = highlightedIndex * cardWidth;
        scrollContainerRef.current.scrollTo({
          left: scrollTo,
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedStockCode, pharmaciesWithPricing]);

  if (pharmaciesWithPricing.length === 0) {
    return null;
  }

  return (
    <div
      ref={scrollContainerRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      className={`flex flex-row gap-4 overflow-x-auto no-scrollbar w-full ${
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      style={{ userSelect: isDragging ? 'none' : 'auto' }}
    >
      {pharmaciesWithPricing.map((pharmacy) => {
        const isHighlighted = highlightedStockCode === pharmacy!.stockCode;
        const isSelected = selectedStockCode === pharmacy!.stockCode;
        
        return (
          <div
            key={pharmacy!.stockCode}
            onClick={() => {
              // Track pharmacy card click (separate from selection)
              const userId = customerState?.customerID || null;
              MainTracker.track("custom", "pharmacy_card_click", {
                user_id: userId,
                pharmacy_id: pharmacy!.stockCode,
                pharmacy_name: pharmacy!.name,
                distance_km: pharmacy!.distance,
                products_available_Price: pharmacy!.pricePerUnit > 0 ? pharmacy!.pricePerUnit : null,
              });
            }}
            className={`bg-white rounded-lg p-4 transition-colors flex-shrink-0 flex flex-col ${
              isHighlighted
                ? 'border-2 border-[#06D1A0] shadow-lg'
                : 'border border-gray-200 hover:border-[#0A9281]'
            }`}
            style={{ 
              width: '300px',
              height: '180px',
              pointerEvents: isDragging ? 'none' : 'auto',
              cursor: 'pointer'
            }}
          >
          <div className="flex flex-row gap-1 flex-grow">
            {/* Pharmacy Info */}
            <div className="flex flex-col gap-1 min-w-0 flex-grow">
              <h4 className="font-semibold text-sm text-[#00302E] line-clamp-1">
                {pharmacy!.name}
              </h4>
              <p className="text-xs text-[#4B5563] line-clamp-2">
                {pharmacy!.address}
              </p>
              {pharmacy!.distance !== null ? (
                <div className="flex items-center text-[10px] text-[#999999]">
                  <span>{pharmacy!.distance.toFixed(1)} {translator.translate(language, "choose_treatment", "default/distance_from_you")}</span>
                </div>
              ) : (
                <div className="h-[14px]"></div>
              )}
            </div>

            {/* Price - only show if pricePerUnit > 0 */}
            {pharmacy!.pricePerUnit > 0 && (
              <div className="flex flex-col flex-shrink-0">
                <span className="text-lg font-bold text-[#00302E]">
                  {getPrice(pharmacy!.pricePerUnit, locale)}
                </span>
                <span className="text-[10px] text-[#707B90]">
                  per {product.items[0]?.custom_quantity_title?.split(' ')?.[1]}
                </span>
              </div>
            )}
          </div>

          {/* Choose Pharmacy Button */}
            <button
              onClick={() => handlePharmacySelect(pharmacy!.stockCode, pharmacy!)}
              disabled={isSelected}
              className={`w-full px-6 py-2 rounded-lg font-medium text-sm border border-solid cursor-pointer mt-auto ${
                isSelected
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : isHighlighted
                  ? 'bg-[#06D1A0] text-white border-[#06D1A0] hover:opacity-90'
                  : 'bg-white text-[#06D1A0] border-[#06D1A0] hover:opacity-80'
              }`}
            >
              {isSelected
                ? 'Selected'
                : translator.translate(language, "choose_treatment", "default/choose_this_pharmacy")}
            </button>

          </div>
      );
      })}
    </div>
  );
};
